import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import * as moment from 'moment';

import { getRelativeDateRangeBounds, propExists, propExistsAndIsNonEmpty } from '../../../../../utils';
import {
  FUTURE_RELATIVE_DATE_RANGE_OPTIONS,
  RELATIVE_DATE_RANGE_OPTIONS,
  RelativeDateRangeOption,
} from '../../../../../models/date-range-filter';

// Mirrors the backend MAX_DAYS_SINCE.
const MAX_DAYS_AGO = 36500;

// The alert must not flash on the intermediate values typed on the way to a valid bound.
const IMPOSSIBLE_RANGE_WARNING_DELAY_MS = 500;

export enum DateRangeFilterMode {
  none = 'none',
  relative = 'relative',
  fixed = 'fixed',
  daysAgo = 'days_ago',
}

/**
 * Reusable date-range filter control (PRO-1248): a radio choice between no
 * filter, a preset period, specific dates and (with `allowDaysAgo`) a days-ago
 * window, two-way bound to the backend `<field>_min` / `_max` / `_range` /
 * `_days_min` / `_days_max` params. Only the active mode's values are kept.
 */
@Component({
  selector: 'app-date-range-filter',
  templateUrl: './date-range-filter.component.html',
  styles: [
    ':host { display: block; }',
    // The two fixed-date inputs share the row evenly and can shrink within a
    // fixed-width container (min-width:0 defeats the input's intrinsic min size).
    '.date-range-fixed-input { flex: 1 1 0; min-width: 0; }',
  ],
})
export class DateRangeFilterComponent implements OnInit, OnChanges, OnDestroy {

  @Input() label = '';

  // Used to build unique element ids/radio-group names when several of these
  // appear on the same page (e.g. multiple date ranges in one filters modal).
  @Input() idPrefix = 'date_range';

  @Input() min: string | undefined;
  @Output() minChange = new EventEmitter<string | undefined>();

  @Input() max: string | undefined;
  @Output() maxChange = new EventEmitter<string | undefined>();

  @Input() range: string | undefined;
  @Output() rangeChange = new EventEmitter<string | undefined>();

  @Input() daysMin: number | undefined;
  @Output() daysMinChange = new EventEmitter<number | undefined>();

  @Input() daysMax: number | undefined;
  @Output() daysMaxChange = new EventEmitter<number | undefined>();

  // Offer the "Days ago" mode; only where the backend declares the
  // `_days_min`/`_days_max` params.
  @Input() allowDaysAgo = false;

  // For time-series charts (PRO-1255), which always need a bounded range:
  // hides the "No filter" option, and when switching from a relative range to
  // fixed dates, prefills those dates with what the relative range currently
  // resolves to, so the chart is unchanged.
  @Input() requireDateRange = false;

  // Fixed dates as a single compact "[start] – [end]" row, so a horizontal
  // filter (the Chart Builder) keeps a constant size between modes.
  @Input() inlineFixedRange = false;

  // The relative range to select when entering relative mode with none set (a
  // chart switching back from fixed dates). With requireDateRange there is no
  // "Select a range…" placeholder, so the range must never be empty.
  @Input() defaultRange: string | undefined;

  public readonly Mode = DateRangeFilterMode;
  public mode: DateRangeFilterMode = DateRangeFilterMode.none;

  // The relative-range options to offer. Defaults to all; a caller can restrict
  // the list (e.g. a chart that only makes sense at weekly/monthly granularity).
  @Input() rangeOptions: RelativeDateRangeOption[] = RELATIVE_DATE_RANGE_OPTIONS;

  // Also offer FUTURE_RELATIVE_DATE_RANGE_OPTIONS, in an "Upcoming" group.
  @Input() includeFutureRanges = false;

  // Set when our own setters emit, so the input echo doesn't re-derive the
  // mode and bounce the radio; external resets (a modal's "Clear All") arrive
  // without it and do re-derive.
  private ignoreNextInputChange = false;

  /** Advisory text when the chosen range cannot match any row; null otherwise. */
  public impossibleRangeWarning: string | null = null;

  private warningTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit() {
    this.mode = this.deriveInitialMode();
    this.refreshImpossibleRangeWarning();
  }

  ngOnDestroy() {
    this.clearWarningTimer();
  }

  ngOnChanges() {
    if (this.ignoreNextInputChange) {
      this.ignoreNextInputChange = false;
      return;
    }
    this.mode = this.deriveInitialMode();
    this.refreshImpossibleRangeWarning();
  }

  public get upcomingRangeOptions(): RelativeDateRangeOption[] {
    return this.includeFutureRanges ? FUTURE_RELATIVE_DATE_RANGE_OPTIONS : [];
  }

  private computeImpossibleRangeWarning(): string | null {
    if (this.mode === DateRangeFilterMode.daysAgo) {
      if (propExists(this.daysMin) && propExists(this.daysMax) && this.daysMin > this.daysMax) {
        return `No results can match: "At least ${this.daysMin} days ago" and "At most ${this.daysMax} days ago" exclude each other. Swap the two numbers to get the days between them.`;
      }
    } else if (this.mode === DateRangeFilterMode.fixed) {
      if (propExistsAndIsNonEmpty(this.min) && propExistsAndIsNonEmpty(this.max)
          && moment(this.min).isAfter(moment(this.max), 'day')) {
        return 'No results can match: "On or after" is later than "On or before".';
      }
    }
    return null;
  }

  // Shows a new warning only once it has persisted for the delay; hides it at once.
  private refreshImpossibleRangeWarning() {
    const next = this.computeImpossibleRangeWarning();
    if (next === null) {
      this.clearWarningTimer();
      this.impossibleRangeWarning = null;
      return;
    }
    if (next === this.impossibleRangeWarning) {
      // A timer still pending for an earlier, different text must not replace it.
      this.clearWarningTimer();
      return;
    }
    this.clearWarningTimer();
    this.warningTimer = setTimeout(() => {
      this.warningTimer = null;
      this.impossibleRangeWarning = next;
    }, IMPOSSIBLE_RANGE_WARNING_DELAY_MS);
  }

  private clearWarningTimer() {
    if (this.warningTimer !== null) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  private deriveInitialMode(): DateRangeFilterMode {
    // Mirror the backend precedence (fixed > days ago > preset) when several
    // arrive together. Days ago is derived even without allowDaysAgo so a
    // URL-loaded value is never silently dropped.
    if (propExistsAndIsNonEmpty(this.min) || propExistsAndIsNonEmpty(this.max)) {
      return DateRangeFilterMode.fixed;
    }
    if (propExists(this.daysMin) || propExists(this.daysMax)) {
      return DateRangeFilterMode.daysAgo;
    }
    if (propExistsAndIsNonEmpty(this.range)) {
      return DateRangeFilterMode.relative;
    }
    // Time-series charts always need a range, so they never sit in "none".
    return this.requireDateRange ? DateRangeFilterMode.relative : DateRangeFilterMode.none;
  }

  public onModeChange(mode: DateRangeFilterMode) {
    const previousRange = this.range;
    this.mode = mode;

    if (mode === DateRangeFilterMode.fixed) {
      // When required (charts), prefill the fixed dates with what the relative
      // range being left currently resolves to, so the result is unchanged.
      // The other modes still clear the values that don't belong to them, so
      // only one mode's values are ever populated.
      if (this.requireDateRange && propExistsAndIsNonEmpty(previousRange)) {
        const bounds = getRelativeDateRangeBounds(previousRange, moment());
        if (bounds !== null) {
          this.setMin(bounds.startDateStr);
          this.setMax(bounds.endDateStr);
        }
      }
      this.setRange(undefined);
      this.setDaysMin(undefined);
      this.setDaysMax(undefined);
    } else if (mode === DateRangeFilterMode.daysAgo) {
      this.setMin(undefined);
      this.setMax(undefined);
      this.setRange(undefined);
    } else if (mode === DateRangeFilterMode.relative) {
      this.setMin(undefined);
      this.setMax(undefined);
      this.setDaysMin(undefined);
      this.setDaysMax(undefined);
      // Charts have no "Select a range…" placeholder, so default the range
      // rather than leaving relative mode with nothing selected.
      if (!propExistsAndIsNonEmpty(this.range) && propExistsAndIsNonEmpty(this.defaultRange)) {
        this.setRange(this.defaultRange);
      }
    } else {
      this.setRange(undefined);
      this.setMin(undefined);
      this.setMax(undefined);
      this.setDaysMin(undefined);
      this.setDaysMax(undefined);
    }
    this.refreshImpossibleRangeWarning();
  }

  public setMin(value: string | undefined) {
    const next = value ? value : undefined;
    this.armIgnoreIfChanged(next !== this.min);
    this.min = next;
    this.minChange.emit(this.min);
    this.refreshImpossibleRangeWarning();
  }

  public setMax(value: string | undefined) {
    const next = value ? value : undefined;
    this.armIgnoreIfChanged(next !== this.max);
    this.max = next;
    this.maxChange.emit(this.max);
    this.refreshImpossibleRangeWarning();
  }

  public setRange(value: string | undefined) {
    const next = value ? value : undefined;
    this.armIgnoreIfChanged(next !== this.range);
    this.range = next;
    this.rangeChange.emit(this.range);
    this.refreshImpossibleRangeWarning();
  }

  // A cleared number input emits null (or '' before ngModel parses it).
  public setDaysMin(value: number | string | null | undefined) {
    const next = this.coerceDays(value);
    this.armIgnoreIfChanged(next !== this.daysMin);
    this.daysMin = next;
    this.daysMinChange.emit(this.daysMin);
    this.refreshImpossibleRangeWarning();
  }

  public setDaysMax(value: number | string | null | undefined) {
    const next = this.coerceDays(value);
    this.armIgnoreIfChanged(next !== this.daysMax);
    this.daysMax = next;
    this.daysMaxChange.emit(this.daysMax);
    this.refreshImpossibleRangeWarning();
  }

  // An emit that changes nothing never reaches ngOnChanges, so arming the flag
  // anyway would swallow the next real external reset.
  private armIgnoreIfChanged(changed: boolean) {
    if (changed) {
      this.ignoreNextInputChange = true;
    }
  }

  // Angular 11 does not enforce min/max/step through ngModel, so the value is
  // normalised here rather than reaching the API as a 400.
  private coerceDays(value: number | string | null | undefined): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const days = Number(value);
    if (isNaN(days)) {
      return undefined;
    }
    return Math.min(Math.max(Math.trunc(days), 0), MAX_DAYS_AGO);
  }
}
